'use client'

import { useState, FormEvent } from 'react'
import { LogIn, User } from 'lucide-react'
import { Language } from '@/lib/brief613/translations'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { panel, headingClass, mutedClass, errorBox, labelClass, fieldClass, primaryBtn } from './ui'

interface ManagerLoginFormProps {
  onSuccess: (manager: {
    id: string
    name: string
    pronom: string | null
    email: string
    phone: string | null
    photoUrl: string | null
    username: string
  }) => void
  language: Language
}

export default function ManagerLoginForm({ onSuccess, language }: ManagerLoginFormProps) {
  const t = useBrief613Translation(language)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!username || !password) {
      setError(t.managerLogin.invalidCredentials)
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/brief613/auth/manager-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      })

      const result = await response.json()

      if (result.success) {
        onSuccess(result.manager)
      } else {
        setError(result.error || t.managerLogin.invalidCredentials)
      }
    } catch (err: any) {
      setError(t.managerLogin.error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className={`${panel} p-8 max-w-md w-full`}>
        <div className="text-center mb-8">
          <User className="mx-auto mb-4 text-white/50" size={32} />
          <h2 className={headingClass}>{t.managerLogin.title}</h2>
          <p className={`${mutedClass} mt-2`}>{t.managerLogin.subtitle}</p>
        </div>

        {error && (
          <div className={`mb-6 ${errorBox}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={labelClass}>{t.managerLogin.username}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className={fieldClass}
              placeholder={t.managerLogin.usernamePlaceholder}
              disabled={loading}
            />
          </div>

          <div>
            <label className={labelClass}>{t.managerLogin.password}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={fieldClass}
              placeholder={t.managerLogin.passwordPlaceholder}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className={primaryBtn}
            disabled={loading}
          >
            {loading ? (
              t.managerLogin.loggingIn
            ) : (
              <>
                <LogIn size={16} />
                {t.managerLogin.loginButton}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
